import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { accountSettingsFieldDescriptionClassName } from "../accountSettingsCardShell";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldClassName,
  accountSettingsFieldLabelStackClassName,
  accountSettingsFieldStackClassName,
} from "./SettingCard";
import { NotificationEventPreferenceRow } from "./NotificationEventPreferenceRow";
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../services/notificationsApi";
import { NotificationPreferences } from "../../../../types/notifications";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import { toast } from "sonner";
import {
  NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_PREFERENCE_EVENT_KEYS,
  type NotificationPreferenceEventKey,
} from "@/constants/notificationEventLabels";

const SUPPORTED_EVENT_KEYS = NOTIFICATION_PREFERENCE_EVENT_KEYS;

const SYSTEM_EVENT_KEYS = new Set<NotificationPreferenceEventKey>([
  "system.broker_disconnected",
  "system.broker_reconnected",
  "system.sync_failed",
  "system.sync_recovered",
]);

const PERFORMANCE_EVENT_KEYS = [
  "performance.daily.new_best_day",
  "performance.daily.new_worst_day",
  "performance.pnl.milestone_crossed",
  "performance.summary.daily",
  "performance.summary.weekly",
  "performance.summary.monthly",
] as const satisfies ReadonlyArray<NotificationPreferenceEventKey>;

const SYSTEM_EVENT_KEYS_LIST = [
  "system.broker_disconnected",
  "system.broker_reconnected",
  "system.sync_failed",
  "system.sync_recovered",
] as const satisfies ReadonlyArray<NotificationPreferenceEventKey>;

/** In-app display titles for each supported notification event key */
const eventLabels = Object.fromEntries(
  SUPPORTED_EVENT_KEYS.map((key) => [key, NOTIFICATION_EVENT_LABELS[key]])
) as Record<NotificationPreferenceEventKey, string>;

type SupportedEventKey = NotificationPreferenceEventKey;

const createDefaultPreferences = (): NotificationPreferences => ({
  inAppEnabled: true,
  emailEnabled: true,
  displayCurrency: "USD",
  supportedCurrencies: [],
  perCategory: {},
  perEvent: Object.fromEntries(
    SUPPORTED_EVENT_KEYS.map((key) => [
      key,
      {
        inApp: true,
        // System notifications are not supported via email
        email: SYSTEM_EVENT_KEYS.has(key) ? false : true,
        accountScope: { mode: "all", accountCodes: [] },
      },
    ])
  ),
});

/** Compare only fields edited on this page (globals + supported per-event prefs). */
function notificationPreferencesDirtySnapshot(prefs: NotificationPreferences): string {
  return JSON.stringify({
    inAppEnabled: prefs.inAppEnabled,
    emailEnabled: prefs.emailEnabled,
    perEvent: Object.fromEntries(
      SUPPORTED_EVENT_KEYS.map((key) => {
        const event = prefs.perEvent[key] ?? {};
        return [
          key,
          {
            inApp: event.inApp ?? true,
            email: SYSTEM_EVENT_KEYS.has(key) ? false : (event.email ?? true),
            accountScope: {
              mode: event.accountScope?.mode ?? "all",
              accountCodes: [...(event.accountScope?.accountCodes ?? [])].sort(),
            },
          },
        ];
      })
    ),
  });
}

function mergeWithDefaults(loaded: NotificationPreferences): NotificationPreferences {
  const defaults = createDefaultPreferences();
  const mergedPerEvent = { ...defaults.perEvent, ...(loaded.perEvent ?? {}) };
  for (const key of SUPPORTED_EVENT_KEYS) {
    const current = mergedPerEvent[key] ?? {};
    mergedPerEvent[key] = {
      inApp: current.inApp ?? true,
      // System notifications are not supported via email
      email: SYSTEM_EVENT_KEYS.has(key) ? false : (current.email ?? true),
      accountScope: {
        mode: current.accountScope?.mode ?? "all",
        accountCodes: current.accountScope?.accountCodes ?? [],
      },
    };
  }
  return {
    inAppEnabled: loaded.inAppEnabled ?? true,
    emailEnabled: loaded.emailEnabled ?? true,
    displayCurrency: (loaded.displayCurrency || "USD").toUpperCase(),
    supportedCurrencies: loaded.supportedCurrencies ?? [],
    perCategory: loaded.perCategory ?? {},
    perEvent: mergedPerEvent,
  };
}

const NotificationSettings: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [savedPrefsSnapshot, setSavedPrefsSnapshot] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedScopeErrorEvent, setSelectedScopeErrorEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { accounts, loading: accountsLoading } = useActiveBrokerAccount();

  const accountCodeOptions = useMemo(
    () => accounts.map((account: { accountCode: string }) => account.accountCode),
    [accounts]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!prefs || savedPrefsSnapshot === null) return false;
    return notificationPreferencesDirtySnapshot(prefs) !== savedPrefsSnapshot;
  }, [prefs, savedPrefsSnapshot]);

  const commitSavedPrefs = (next: NotificationPreferences) => {
    const merged = mergeWithDefaults(next);
    setPrefs(merged);
    setSavedPrefsSnapshot(notificationPreferencesDirtySnapshot(merged));
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await fetchNotificationPreferences();
        if (isMounted) {
          commitSavedPrefs(data);
        }
      } catch (error) {
        console.error("Failed to load notification preferences:", error);
        if (isMounted) {
          commitSavedPrefs(createDefaultPreferences());
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggleGlobal = (key: "inAppEnabled" | "emailEnabled") => {
    if (!prefs) return;
    const nextEnabled = !prefs[key];

    // Behavior:
    // - Turning a global channel OFF disables it everywhere (sets per-event channel false).
    // - Turning a global channel ON does NOT force-enable all events.
    if (key === "emailEnabled") {
      if (!nextEnabled) {
        const nextPerEvent = { ...prefs.perEvent };
        for (const eventKey of SUPPORTED_EVENT_KEYS) {
          const current = nextPerEvent[eventKey] ?? {
            inApp: true,
            email: true,
            accountScope: { mode: "all", accountCodes: [] },
          };
          nextPerEvent[eventKey] = {
            ...current,
            email: SYSTEM_EVENT_KEYS.has(eventKey) ? false : false,
          };
        }
        setPrefs({ ...prefs, emailEnabled: false, perEvent: nextPerEvent });
        return;
      }
      setPrefs({ ...prefs, emailEnabled: true });
      return;
    }

    if (key === "inAppEnabled") {
      if (!nextEnabled) {
        const nextPerEvent = { ...prefs.perEvent };
        for (const eventKey of SUPPORTED_EVENT_KEYS) {
          const current = nextPerEvent[eventKey] ?? {
            inApp: true,
            email: true,
            accountScope: { mode: "all", accountCodes: [] },
          };
          nextPerEvent[eventKey] = {
            ...current,
            inApp: false,
          };
        }
        setPrefs({ ...prefs, inAppEnabled: false, perEvent: nextPerEvent });
        return;
      }
      setPrefs({ ...prefs, inAppEnabled: true });
      return;
    }
  };

  const handleToggleEvent = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    field: "inApp" | "email"
  ) => {
    if (!prefs) return;
    if (field === "email" && SYSTEM_EVENT_KEYS.has(eventKey)) return;
    const current = prefs.perEvent[eventKey] || {
      inApp: true,
      email: true,
      accountScope: { mode: "all", accountCodes: [] },
    };

    // If global channel is OFF and user enables a single event, auto-enable global channel
    // without changing other events.
    const nextGlobalPatch: Partial<NotificationPreferences> = {};
    if (field === "email" && !prefs.emailEnabled && current.email === false) {
      nextGlobalPatch.emailEnabled = true;
    }
    if (field === "inApp" && !prefs.inAppEnabled && current.inApp === false) {
      nextGlobalPatch.inAppEnabled = true;
    }

    setPrefs({
      ...prefs,
      ...nextGlobalPatch,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          [field]: !current[field],
        },
      },
    });
  };

  const handleScopeModeChange = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    mode: "all" | "selected"
  ) => {
    if (!prefs) return;
    const current = prefs.perEvent[eventKey] || {};
    setSelectedScopeErrorEvent((prev) => (prev === eventKey ? null : prev));
    setPrefs({
      ...prefs,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          accountScope: {
            mode,
            accountCodes: current.accountScope?.accountCodes ?? [],
          },
        },
      },
    });
  };

  const handleScopeAccountsChange = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    accountCodes: string[]
  ) => {
    if (!prefs) return;
    const current = prefs.perEvent[eventKey] || {};
    setSelectedScopeErrorEvent((prev) => (prev === eventKey ? null : prev));
    setPrefs({
      ...prefs,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          accountScope: {
            mode: current.accountScope?.mode ?? "selected",
            accountCodes,
          },
        },
      },
    });
  };

  const handleSave = async () => {
    if (!prefs) return;
    for (const eventKey of SUPPORTED_EVENT_KEYS) {
      const eventPref = prefs.perEvent[eventKey];
      if (
        eventPref?.accountScope?.mode === "selected" &&
        (!eventPref.accountScope.accountCodes || eventPref.accountScope.accountCodes.length === 0)
      ) {
        setSelectedScopeErrorEvent(eventKey);
        toastError("Select at least one account", { description: `${eventLabels[eventKey]} is set to selected accounts.` });
        return;
      }
    }

    setSaving(true);
    try {
      const updated = await updateNotificationPreferences(prefs);
      commitSavedPrefs(updated);
      toast.success("Notification preferences saved");
    } catch (error) {
      console.error("Failed to save notification preferences:", error);
      presentMutationError(error, "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
      </div>
    );
  }

  const renderEventPreferences = (
    eventKeys: readonly SupportedEventKey[],
    options?: { showEmailToggle?: boolean }
  ) => (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {eventKeys.map((key) => {
        const current = prefs.perEvent[key] ?? {
          inApp: true,
          email: SYSTEM_EVENT_KEYS.has(key) ? false : true,
          accountScope: { mode: "all", accountCodes: [] },
        };
        const selectedCodes = current.accountScope?.accountCodes ?? [];
        const mode = current.accountScope?.mode ?? "all";
        const hasScopeError =
          selectedScopeErrorEvent === key && mode === "selected" && selectedCodes.length === 0;
        const showEmailToggle = options?.showEmailToggle ?? !SYSTEM_EVENT_KEYS.has(key);

        return (
          <NotificationEventPreferenceRow
            key={key}
            eventKey={key}
            title={eventLabels[key]}
            inApp={current.inApp ?? true}
            email={current.email ?? !SYSTEM_EVENT_KEYS.has(key)}
            showEmailToggle={showEmailToggle}
            mode={mode}
            selectedCodes={selectedCodes}
            hasScopeError={hasScopeError}
            accountsLoading={accountsLoading}
            accountCodeOptions={accountCodeOptions}
            onToggleInApp={() => handleToggleEvent(key, "inApp")}
            onToggleEmail={() => handleToggleEvent(key, "email")}
            onScopeModeChange={(nextMode) => handleScopeModeChange(key, nextMode)}
            onScopeAccountsChange={(codes) => handleScopeAccountsChange(key, codes)}
          />
        );
      })}
    </div>
  );

  return (
    <div
      className={accountSettingsCardStackClassName}
      data-notification-settings=""
      data-account-settings-card-stack=""
    >
      <SettingCard
        title="Global notification channels"
        description="Control whether Tessera can send you in-app and email notifications."
      >
        <div className={accountSettingsFieldStackClassName}>
          <Field className={accountSettingsFieldClassName}>
            <div className="flex items-center justify-between gap-3">
              <div
                data-account-settings-field-label-stack=""
                className={accountSettingsFieldLabelStackClassName}
              >
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>In-app notifications</FieldLabel>
                <FieldDescription className={accountSettingsFieldDescriptionClassName}>
                  Show notifications in the in-app notifications centre and sidebar.
                </FieldDescription>
              </div>
              <Switch
                checked={prefs.inAppEnabled}
                onCheckedChange={() => handleToggleGlobal("inAppEnabled")}
              />
            </div>
          </Field>

          <Field className={accountSettingsFieldClassName}>
            <div className="flex items-center justify-between gap-3">
              <div
                data-account-settings-field-label-stack=""
                className={accountSettingsFieldLabelStackClassName}
              >
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Email notifications</FieldLabel>
                <FieldDescription className={accountSettingsFieldDescriptionClassName}>
                  Allow Tessera to send you email notifications.
                </FieldDescription>
              </div>
              <Switch
                checked={prefs.emailEnabled}
                onCheckedChange={() => handleToggleGlobal("emailEnabled")}
              />
            </div>
          </Field>
        </div>
      </SettingCard>

      <SettingCard
        title="Per-event preferences"
        description="Choose channel and account scope for each event."
      >
        <Tabs defaultValue="performance" data-market-context-buckets-tabs="" className="w-full">
          <div className="w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="min-w-max">
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="broker">Broker</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="performance" className="mt-0 pt-4">
            {renderEventPreferences(PERFORMANCE_EVENT_KEYS)}
          </TabsContent>
          <TabsContent value="broker" className="mt-0 pt-4">
            {renderEventPreferences(SYSTEM_EVENT_KEYS_LIST, { showEmailToggle: false })}
          </TabsContent>
        </Tabs>
      </SettingCard>

      {hasUnsavedChanges ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            disabled={saving}
            aria-busy={saving || undefined}
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            Save preferences
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationSettings;

